<div class="security-page">
  <form class="security-card" [formGroup]="changePasswordForm" autocomplete="off">
    <div class="card-heading">
      <span class="card-title">Change Password</span>
      <span class="card-subtitle">Update your password to keep your account secure.</span>
    </div>

    <div class="field">
      <label class="field-label" for="oldPassword">
        <span class="status-span">*</span> Current Password
      </label>
      <div class="password-input-wrapper">
        <input
          id="oldPassword"
          formControlName="oldPassword"
          [type]="showOldPassword ? 'text' : 'password'"
          name="oldPassword"
          class="field-input password-input"
          autocomplete="current-password"
          placeholder="Enter current password"
          [ngClass]="{
            'input-error':
              changePasswordForm.get('oldPassword')?.invalid &&
              (changePasswordForm.get('oldPassword')?.dirty ||
                changePasswordForm.get('oldPassword')?.touched),
          }"
          (copy)="$event.preventDefault()"
          (paste)="$event.preventDefault()"
        />
        <button
          type="button"
          class="password-toggle-btn"
          (click)="togglePasswordVisibility('old')"
          [attr.aria-label]="showOldPassword ? 'Hide password' : 'Show password'"
        >
          <i [class]="showOldPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
        </button>
      </div>
      <!-- Error for empty type -->
      @if (
        changePasswordForm.get('oldPassword')?.errors?.['required'] &&
        (changePasswordForm.get('oldPassword')?.dirty ||
          changePasswordForm.get('oldPassword')?.touched)
      ) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Current password cannot be empty.</p>
        </div>
      } @else if (changePasswordForm.get('oldPassword')?.errors?.['minlength']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Current password must be at least 8 characters.</p>
        </div>
      }
    </div>

    <div class="field">
      <label class="field-label" for="newPassword">
        <span class="status-span">*</span> New Password
      </label>
      <div class="password-input-wrapper">
        <input
          id="newPassword"
          formControlName="newPassword"
          [type]="showNewPassword ? 'text' : 'password'"
          name="newPassword"
          class="field-input password-input"
          autocomplete="new-password"
          placeholder="Enter new password"
          (input)="onPasswordInput()"
          [ngClass]="{
            'input-error':
              changePasswordForm.get('newPassword')?.invalid &&
              (changePasswordForm.get('newPassword')?.dirty ||
                changePasswordForm.get('newPassword')?.touched),
          }"
          (copy)="$event.preventDefault()"
          (paste)="$event.preventDefault()"
        />
        <button
          type="button"
          class="password-toggle-btn"
          (click)="togglePasswordVisibility('new')"
          [attr.aria-label]="showNewPassword ? 'Hide password' : 'Show password'"
        >
          <i [class]="showNewPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
        </button>
      </div>
      @if (changePasswordForm.get('newPassword')?.value?.length > 0) {
        <div class="strength-meter">
          <div
            class="strength-bar"
            [class.weak]="passwordStrength === 1"
            [class.fair]="passwordStrength === 2"
            [class.good]="passwordStrength === 3"
            [class.strong]="passwordStrength === 4"
            [class.very-strong]="passwordStrength === 5"
          ></div>
        </div>
      }
      @if (
        changePasswordForm.get('newPassword')?.errors?.['required'] &&
        (changePasswordForm.get('newPassword')?.dirty ||
          changePasswordForm.get('newPassword')?.touched)
      ) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>New password cannot be empty.</p>
        </div>
      } @else if (changePasswordForm.get('newPassword')?.errors?.['minlength']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>New password must be at least 8 characters.</p>
        </div>
      } @else if (changePasswordForm.get('newPassword')?.errors?.['pattern']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>
            New password must include upper and lower case letters, a number, and a special
            character (&#64;$!%*?&amp;).
          </p>
        </div>
      } @else if (
        changePasswordForm.get('newPassword')?.errors?.['sameAsOldPassword'] &&
        (changePasswordForm.get('newPassword')?.dirty ||
          changePasswordForm.get('newPassword')?.touched)
      ) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>New password cannot be the same as the old password.</p>
        </div>
      }
    </div>

    <div class="field">
      <label class="field-label" for="confirmPassword">
        <span class="status-span">*</span> Confirm Password
      </label>
      <div class="password-input-wrapper">
        <input
          id="confirmPassword"
          formControlName="confirmPassword"
          [type]="showConfirmPassword ? 'text' : 'password'"
          name="confirmPassword"
          class="field-input password-input"
          autocomplete="new-password"
          placeholder="Confirm new password"
          [ngClass]="{
            'input-error':
              changePasswordForm.get('confirmPassword')?.invalid &&
              (changePasswordForm.get('confirmPassword')?.dirty ||
                changePasswordForm.get('confirmPassword')?.touched),
          }"
          (copy)="$event.preventDefault()"
          (paste)="$event.preventDefault()"
        />
        <button
          type="button"
          class="password-toggle-btn"
          (click)="togglePasswordVisibility('confirm')"
          [attr.aria-label]="showConfirmPassword ? 'Hide password' : 'Show password'"
        >
          <i [class]="showConfirmPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
        </button>
      </div>
      @if (
        changePasswordForm.get('confirmPassword')?.errors?.['required'] &&
        (changePasswordForm.get('confirmPassword')?.dirty ||
          changePasswordForm.get('confirmPassword')?.touched)
      ) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Confirm password cannot be empty.</p>
        </div>
      } @else if (changePasswordForm.errors?.['passwordsMismatch']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Passwords do not match.</p>
        </div>
      }
    </div>

    <div class="policy-note">
      <i class="bx bx-lock-alt"></i>
      <span>
        Strong passwords require at least 8 characters, including a mix of letters, numbers, and
        special characters.
      </span>
    </div>

    <div class="card-actions">
      <button type="button" class="cancel-btn" (click)="changePasswordForm.reset()">Cancel</button>
      <button
        type="button"
        class="update-btn"
        [disabled]="!changePasswordForm.valid"
        (click)="onSubmit()"
      >
        Update Password
      </button>
    </div>
  </form>
</div>
